import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled, Tooltip } from "@mui/material";
import {
  PredefinedInsight,
  QuestionOptions,
} from "dialogue/src/graphql/types/graphql";
import useSessionStore from "dialogue/src/context/useSessionStore";
import { MemoizedAnswer } from "dialogue/src/components/Answer";
import { SkeletonLoader } from "../loader/skeletonLoader";
import ChatContainer from "dialogue/src/components/UI/ChatContainer";
import { convertToMarkdownList } from "../references/utils";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import useStreamManager from "dialogue/src/wsocket/wsocket";
import useScrollDetect, {
  useAutoScroll,
} from "dialogue/src/utils/useScrollDetect";
import { clinicScrollableStyle } from "fe-common/ui/commonStyles";
import {
  ChatMessage,
  MessageType,
  QuestionType,
} from "dialogue/src/types/types";

//region [[ Styles ]]

const PatientCardAnswerPromptView = styled("div")({
  overflow: "auto",
  // ".clinic-tab-panel & .scrollbar-container": {
  //   paddingTop: 12,
  // },
  padding: "0 5px",
  ...clinicScrollableStyle,
});
const SkeletonLoaderView = styled("div")({
  ".clinic-tab-panel &": {
    paddingTop: 20,
  },
});
const CustomPromptWarning = styled("div")({
  display: "flex",
  gap: 4,
  marginBottom: 12,
  fontSize: 12,
  width: "100%",
  alignItems: "center",
  background: "#ffcc5d",
  padding: "6px",
  position: "sticky",
  top: 0,
  zIndex: 1000,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientCardAnswerPromptProps {
  questionType?: QuestionType;
  prompt?: string;
}

const options: QuestionOptions = {
  guidelineId: null,
  forceGuidelines: false,
  disableGuidelines: false,
};

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const PatientCardAnswerPrompt = ({
  questionType,
  prompt,
}: PatientCardAnswerPromptProps) => {
  const {
    loadingRagSettings,
    loadingChatSession,
    ragSettings,
    askPromptInSession,
    sessionId,
    wsSessionId,
    insights,
    sessionUser,
    customPrompts,
  } = useSessionStore();

  const { getStreamById } = useStreamManager(
    ragSettings.wssEndpoint,
    sessionUser.allowDebug,
  );

  const [answer, setAnswer] = useState<ChatMessage>(null);
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const contentHolderRef: React.MutableRefObject<HTMLDivElement> = useRef();
  const autoScrollRef: React.MutableRefObject<boolean> = useRef(true);

  const [autoScrollOn] = useAutoScroll(contentHolderRef.current);

  useScrollDetect(
    contentHolderRef.current,
    (scrollAmount) => {
      if (scrollAmount < -25) {
        autoScrollRef.current = false; //stop autoscroll when user scrolls up
      }
    },
    null,
    () => {
      autoScrollOn(); //activate autoscroll when user scrolls to bottom
    },
  );

  const predefinedInsight = useMemo(
    () =>
      Object.values(PredefinedInsight).includes(
        questionType as PredefinedInsight,
      )
        ? (questionType as PredefinedInsight)
        : null,
    [questionType],
  );

  const handleStream = useCallback(
    (streamId: string) => {
      return getStreamById(wsSessionId, streamId);
    },
    [wsSessionId, getStreamById],
  );

  const getGuidelineWithPrompt = useCallback(
    async (index: number, guidelineId: string): Promise<ChatMessage> => {
      setAnswer((prevState) => ({ ...prevState, text: "" })); // clear answer text guidelineId changes
      return askPromptInSession(
        options,
        predefinedInsight,
        prompt || customPrompt,
        prompt || customPrompt ? questionType : null,
        guidelineId,
      );
    },
    [askPromptInSession, options, questionType, customPrompt],
  );

  const getAnswer = useCallback(() => {
    let promptText: string = prompt;

    if (!promptText && questionType) {
      // check if there is a custom prompt for this question type in clinic settings
      if (customPrompts && customPrompts[questionType]) {
        promptText = customPrompts[questionType];
        setCustomPrompt(promptText);
      }
    }

    setLoading(true);
    askPromptInSession(
      options,
      predefinedInsight,
      promptText,
      promptText ? questionType : null,
    )
      .then((promptAnswer) => {
        if (!promptAnswer) setError(true);
        else setAnswer(promptAnswer);
      })
      .finally(() => setLoading(false));
  }, [prompt, questionType, customPrompts]);

  useEffect(() => {
    setAnswer({
      index: 0,
      type: MessageType.answer,
      text: "",
    }); //if insightType or case changes put a placeholder
  }, [sessionId, questionType]);

  useEffect(() => {
    if (error || loading || !sessionId) return;
    //get answer from save insights or from server
    if (questionType && insights?.[questionType]?.text) {
      let promptAnswer = insights[questionType];
      if (questionType === PredefinedInsight.Dd)
        promptAnswer = {
          ...promptAnswer,
          text: convertToMarkdownList(promptAnswer?.text),
        };
      setAnswer(promptAnswer);
    } else {
      getAnswer();
    }
  }, [sessionId, askPromptInSession, customPrompts]);

  if (loadingRagSettings || loadingChatSession || !answer)
    return (
      <ChatContainer embedded={true} rtlDisplay={ragSettings.rtlDisplay}>
        <SkeletonLoaderView>
          <SkeletonLoader />
        </SkeletonLoaderView>
      </ChatContainer>
    );

  return (
    <ChatContainer embedded={true} rtlDisplay={ragSettings.rtlDisplay}>
      <PatientCardAnswerPromptView
        ref={contentHolderRef}
        id={"answer-prompt-container"}
      >
        {answer && (
          <>
            {sessionUser?.allowDebug && customPrompt!! && (
              <Tooltip title={customPrompt} arrow placement={"bottom-start"}>
                <CustomPromptWarning className={"custom-prompt-warning"}>
                  <ReportGmailerrorredIcon />
                  {`This clinic uses a custom prompt for ${questionType}`}
                </CustomPromptWarning>
              </Tooltip>
            )}
            <MemoizedAnswer
              message={answer}
              getStream={handleStream}
              debugMode={ragSettings?.debugMode}
              standalone={true}
              usedPrompt={{
                customPrompt: customPrompt,
                predefinedInsight: predefinedInsight,
              }}
              questionType={questionType}
              getGuideline={getGuidelineWithPrompt}
              question={null}
            />
          </>
        )}
      </PatientCardAnswerPromptView>
    </ChatContainer>
  );
};
